import { Seo } from '@/components/Seo';
import { JsonLd, makeFAQSchema, makeBreadcrumbSchema } from '@/components/Schema';
import { FAQ } from '@/components/FAQ';
import { FinalCTA } from '@/components/FinalCTA';
import { Link } from 'react-router-dom';
import { Check, X, Crown } from 'lucide-react';

const faqs = [
  {
    question: 'Is the free plan really free?',
    answer: 'Yes. You can build, edit, and download unlimited resumes and use up to 2 AI rewrites per browser ID without paying.',
  },
  {
    question: 'What do I get with Pro?',
    answer: 'Pro unlocks 50 AI rewrites per day, all premium templates, and priority support.',
  },
  {
    question: 'Can I cancel anytime?',
    answer: 'Yes. Monthly subscriptions can be canceled at any time.',
  },
  {
    question: 'Is there a free trial?',
    answer: 'We do not offer a 7-day free trial. The Free plan lets you test the core experience before upgrading.',
  },
  {
    question: 'What happens to my data?',
    answer: 'Your resume data stays in your browser. We do not store it on our servers.',
  },
];

const plans = [
  {
    name: 'Free',
    price: '$0',
    period: 'forever',
    description: 'Build and download unlimited resumes.',
    features: [
      { text: 'Unlimited resume downloads', included: true },
      { text: 'ATS-friendly templates', included: true },
      { text: '2 AI rewrites per browser ID', included: true },
      { text: 'Local storage privacy', included: true },
      { text: 'DOCX export', included: false },
      { text: 'ATS keyword check', included: false },
    ],
    cta: 'Get Started Free',
    to: '/app/create',
    primary: false,
  },
  {
    name: 'Pro',
    price: '$8.99',
    period: '/ month',
    description: 'More AI power and premium templates.',
    features: [
      { text: '50 AI rewrites per day', included: true },
      { text: 'All premium templates', included: true },
      { text: 'Priority support', included: true },
      { text: 'DOCX export', included: true },
      { text: 'ATS keyword check', included: true },
      { text: 'Everything in Free', included: true },
    ],
    cta: 'Upgrade to Pro',
    to: '/pricing',
    primary: true,
  },
  {
    name: 'Lifetime',
    price: '$99',
    period: 'one-time',
    description: 'Pay once, use for 36 months.',
    features: [
      { text: 'Same as Pro for 36 months', included: true },
      { text: 'All current and future templates within 12 months', included: true },
      { text: 'Priority support', included: true },
      { text: 'One-time payment', included: true },
      { text: 'Monthly billing', included: false },
      { text: 'Renewals', included: false },
    ],
    cta: 'Get Lifetime',
    to: '/pricing',
    primary: false,
  },
];

export function Pricing() {
  return (
    <>
      <Seo
        title="Pricing | Free Resume Builder & Pro Plans"
        description="Build your resume for free. Upgrade to Pro for more AI rewrites and premium templates. No hidden fees."
        path="/pricing"
      />
      <JsonLd schema={makeFAQSchema(faqs)} />
      <JsonLd schema={makeBreadcrumbSchema([{ name: 'Pricing', path: '/pricing' }])} />

      <section className="hero-glow py-24 md:py-32">
        <div className="mx-auto max-w-[800px] px-6 text-center">
          <h1 className="text-balance text-4xl font-extrabold text-white md:text-5xl">
            Simple, Transparent Pricing
          </h1>
          <p className="mx-auto mt-6 max-w-[680px] text-lg text-white/75">
            Build and download resumes for free. Upgrade only if you want more AI power and premium templates.
          </p>
          <Link
            to="/app/create"
            className="btn-secondary-dark mt-8 inline-flex items-center justify-center rounded-[10px] px-8 py-4 text-base font-semibold"
          >
            Create Free Resume
          </Link>
        </div>
      </section>

      <section className="bg-[#0A2540] py-16 md:py-24">
        <div className="mx-auto max-w-[1100px] px-6">
          <div className="grid gap-6 md:grid-cols-3">
            {plans.map((plan) => (
              <div
                key={plan.name}
                className={`relative overflow-hidden rounded-2xl p-8 ${
                  plan.primary
                    ? 'scale-[1.03] border border-[#00D4AA]/30 bg-[#112940] shadow-2xl'
                    : 'glass-card'
                }`}
              >
                {plan.primary && (
                  <div className="gradient-premium absolute left-0 right-0 top-0 h-1" />
                )}
                {plan.name === 'Lifetime' && (
                  <div className="absolute right-4 top-4 inline-flex items-center gap-1 rounded bg-[#635BFF] px-2 py-1 text-xs font-bold text-white">
                    <Crown size={10} /> Limited time
                  </div>
                )}
                <div className="text-left">
                  <h3 className="text-sm font-semibold uppercase tracking-wider text-white/65">{plan.name}</h3>
                  <div className="mt-4 flex items-baseline gap-2">
                    <span className="text-5xl font-extrabold text-[#00D4AA]">{plan.price}</span>
                    <span className="text-base text-white/65">{plan.period}</span>
                  </div>
                  <p className="mt-4 text-[15px] text-white/75">{plan.description}</p>
                </div>
                <ul className="mt-6 space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature.text} className="flex items-start gap-3 text-[15px] text-white/75">
                      {feature.included ? (
                        <Check size={18} className="mt-0.5 shrink-0 text-[#00D4AA]" />
                      ) : (
                        <X size={18} className="mt-0.5 shrink-0 text-white/45" />
                      )}
                      {feature.text}
                    </li>
                  ))}
                </ul>
                <Link
                  to={plan.to}
                  className={`mt-8 inline-flex w-full items-center justify-center rounded-[10px] px-6 py-3 text-base font-semibold ${
                    plan.primary
                      ? 'btn-primary'
                      : plan.name === 'Lifetime'
                        ? 'bg-[#635BFF] text-white transition hover:bg-[#4F46E5]'
                        : 'btn-secondary-dark'
                  }`}
                >
                  {plan.cta}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16">
        <div className="mx-auto max-w-[800px] px-6">
          <div className="text-center">
            <span className="rounded-full bg-[#E7F1FA] px-4 py-1.5 text-sm font-semibold text-[#0A2540]">FAQ</span>
            <h2 className="mt-4 text-3xl font-bold text-[#0A2540]">Pricing FAQ</h2>
          </div>
          <div className="mt-10">
            <FAQ items={faqs} />
          </div>
        </div>
      </section>

      <FinalCTA
        title="Start free today"
        subtitle="No credit card. No sign-up. Upgrade only when you want more."
        buttonText="Create Free Resume"
        buttonTo="/app/create"
      />
    </>
  );
}

export default Pricing;
